<template>
  <div class="bg-gray-900 rounded-2xl shadow-2xl w-full max-w-6xl mx-auto p-6 md:p-8 transform transition-all hover:scale-[1.002]">
    <div class="mb-6 flex flex-col md:flex-row justify-between items-center text-white">
      <h2 class="text-2xl font-bold mb-4 md:mb-0">{{ $t('flight_form.title') }}</h2>
      
      <!-- Trip Type -->
      <div class="flex space-x-6">
        <label class="flex items-center space-x-2 cursor-pointer group">
          <div class="relative flex items-center justify-center p-0.5 rounded-full border border-white/50 group-hover:border-primary transition-colors">
             <input type="radio" v-model="form.tripType" value="roundtrip" class="appearance-none h-3 w-3 rounded-full checked:bg-primary transition-colors">
          </div>
          <span class="text-gray-300 font-medium group-hover:text-white transition-colors">{{ $t('flight_form.roundtrip') }}</span>
        </label>
        <label class="flex items-center space-x-2 cursor-pointer group">
          <div class="relative flex items-center justify-center p-0.5 rounded-full border border-white/50 group-hover:border-primary transition-colors">
             <input type="radio" v-model="form.tripType" value="oneway" class="appearance-none h-3 w-3 rounded-full checked:bg-primary transition-colors">
          </div>
          <span class="text-gray-300 font-medium group-hover:text-white transition-colors">{{ $t('flight_form.oneway') }}</span>
        </label>
        <label class="flex items-center space-x-2 cursor-pointer group">
          <div class="relative flex items-center justify-center p-0.5 rounded-full border border-white/50 group-hover:border-primary transition-colors">
             <input type="radio" v-model="form.tripType" value="multicity" class="appearance-none h-3 w-3 rounded-full checked:bg-primary transition-colors">
          </div>
          <span class="text-gray-300 font-medium group-hover:text-white transition-colors">{{ $t('flight_form.multicity') }}</span>
        </label>
      </div>
    </div>

    <div class="space-y-4">
        <div v-for="(leg, index) in form.legs" :key="index" class="grid grid-cols-1 md:grid-cols-12 gap-4 relative items-end">
          <div v-if="form.tripType === 'multicity'" class="md:col-span-12 text-sm text-gray-400 font-bold uppercase mb-1">
              {{ $t('flight_form.leg') }} {{ index + 1 }}
              <button v-if="index > 1" @click="removeLeg(index)" class="text-red-500 hover:text-red-400 ml-2 text-xs uppercase">{{ $t('flight_form.remove_leg') }}</button>
          </div>

          <!-- Locations -->
          <div class="md:col-span-3">
            <div class="relative group">
              <label class="block text-xs font-bold text-gray-400 uppercase mb-1">{{ $t('flight_form.origin') }}</label>
              <div class="relative">
                  <div class="flex items-center bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2.5 focus-within:ring-2 focus-within:ring-primary focus-within:border-primary transition-all hover:border-gray-500">
                     <span class="text-gray-400 mr-2 material-icons text-sm" :class="{'animate-spin text-primary': loadingSearch[index] && loadingSearch[index].origin}">
                        {{ (loadingSearch[index] && loadingSearch[index].origin) ? 'sync' : 'flight_takeoff' }}
                     </span>
                     <input 
                        type="text" 
                        v-model="leg.origin" 
                        @input="searchAirports($event.target.value, 'origin', index)" 
                        :placeholder="$t('flight_form.placeholder_city')" 
                        class="bg-transparent w-full outline-none text-white placeholder-gray-500 font-medium"
                     >
                  </div>
                  <!-- Dropdown -->
                  <div v-if="results[index] && results[index].origin && results[index].origin.length > 0" class="absolute z-50 w-full bg-white shadow-xl rounded-lg mt-1 max-h-60 overflow-y-auto border border-gray-100 overflow-hidden">
                      <!-- Result Counter -->
                      <div v-if="results[index].origin.length > maxVisibleResults" class="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium border-b border-gray-200">
                          {{ $t('flight_form.showing_results', { msg1: maxVisibleResults, msg2: results[index].origin.length }) }}
                      </div>
                      <!-- Results -->
                      <div 
                        v-for="airport in getVisibleResults(results[index].origin)" 
                        :key="airport.id" 
                        @click="selectAirport(airport, 'origin', index)"
                        class="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-50 last:border-0 transition-colors"
                      >
                          <div class="font-bold text-gray-900">{{ airport.city }} ({{ airport.code }})</div>
                          <div class="text-xs text-gray-500">{{ airport.name }}</div>
                      </div>
                  </div>
              </div>
            </div>
          </div>

          <div class="md:col-span-3">
            <div class="relative group">
               <label class="block text-xs font-bold text-gray-400 uppercase mb-1">{{ $t('flight_form.destination') }}</label>
               <div class="relative">
                  <div class="flex items-center bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2.5 focus-within:ring-2 focus-within:ring-primary focus-within:border-primary transition-all hover:border-gray-500">
                     <span class="text-gray-400 mr-2 material-icons text-sm" :class="{'animate-spin text-primary': loadingSearch[index] && loadingSearch[index].destination}">
                        {{ (loadingSearch[index] && loadingSearch[index].destination) ? 'sync' : 'flight_land' }}
                     </span>
                     <input 
                        type="text" 
                        v-model="leg.destination" 
                         @input="searchAirports($event.target.value, 'destination', index)"
                        :placeholder="$t('flight_form.placeholder_city')" 
                        class="bg-transparent w-full outline-none text-white placeholder-gray-500 font-medium"
                     >
                  </div>
                  <!-- Dropdown -->
                  <div v-if="results[index] && results[index].destination && results[index].destination.length > 0" class="absolute z-50 w-full bg-white shadow-xl rounded-lg mt-1 max-h-60 overflow-y-auto border border-gray-100 overflow-hidden">
                      <!-- Result Counter -->
                      <div v-if="results[index].destination.length > maxVisibleResults" class="px-4 py-2 bg-gray-50 text-xs text-gray-600 font-medium border-b border-gray-200">
                          {{ $t('flight_form.showing_results', { msg1: maxVisibleResults, msg2: results[index].destination.length }) }}
                      </div>
                      <!-- Results -->
                      <div 
                        v-for="airport in getVisibleResults(results[index].destination)" 
                        :key="airport.id" 
                         @click="selectAirport(airport, 'destination', index)"
                        class="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm border-b border-gray-50 last:border-0 transition-colors"
                      >
                           <div class="font-bold text-gray-900">{{ airport.city }} ({{ airport.code }})</div>
                          <div class="text-xs text-gray-500">{{ airport.name }}</div>
                      </div>
                  </div>
               </div>
            </div>
          </div>

          <!-- Dates -->
          <!-- If Roundtrip and first leg, show Range Picker -->
          <div v-if="form.tripType === 'roundtrip' && index === 0" class="md:col-span-4">
               <label class="block text-xs font-bold text-gray-400 uppercase mb-1">{{ $t('flight_form.dates') }}</label>
               <date-picker v-model="form.dateRange" range type="date" value-type="format" format="YYYY-MM-DD" :placeholder="$t('flight_form.dates')" input-class="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2.5 outline-none text-white font-medium hover:border-gray-500 focus:ring-2 focus:ring-primary transition-all h-[46px]"></date-picker>
          </div>

          <!-- Else show Single Date Picker -->
          <div v-else class="md:col-span-4">
               <label class="block text-xs font-bold text-gray-400 uppercase mb-1">{{ $t('flight_form.dates') }}</label>
               <date-picker v-model="leg.date" value-type="format" format="YYYY-MM-DD" :placeholder="$t('flight_form.placeholder_date_departure')" input-class="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2.5 outline-none text-white font-medium hover:border-gray-500 focus:ring-2 focus:ring-primary transition-all h-[46px]"></date-picker>
          </div>

          <!-- Submit Button (Only on last leg if not multi) or Pax (Only on first leg) -->
          <div v-if="index === 0" class="md:col-span-2 relative">
             <label class="block text-xs font-bold text-gray-400 uppercase mb-1">{{ $t('flight_form.passengers') }}</label>
             <button @click="togglePaxDropdown" class="w-full bg-gray-800/50 border border-gray-600 rounded-lg px-3 py-2.5 text-left flex items-center justify-between outline-none focus:ring-2 focus:ring-primary hover:border-gray-500 text-white h-[46px]">
                <div class="flex items-center">
                     <span class="material-icons text-gray-400 mr-2 text-sm">person</span>
                     <span class="font-medium text-sm">{{ totalPax }}</span>
                </div>
                <span class="text-gray-400 text-xs">▼</span>
             </button>

             <!-- Dropdown Menu -->
              <div v-if="showPaxDropdown" class="absolute top-full right-0 mt-2 w-72 bg-white rounded-xl shadow-2xl z-50 p-4 border border-gray-100 animate-fade-in-down text-gray-800">
                 <div class="space-y-4">
                    <!-- Adults -->
                    <div class="flex justify-between items-center">
                       <div>
                          <div class="font-bold text-gray-700">{{ $t('flight_form.adults') }}</div>
                          <div class="text-xs text-gray-500">{{ $t('flight_form.adults_desc') }}</div>
                       </div>
                       <div class="flex items-center space-x-3">
                          <button @click="updatePax('adults', -1)" class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center font-bold hover:bg-gray-100 transition-colors" :disabled="form.pax.adults <= 1">-</button>
                          <span class="w-4 text-center font-bold">{{ form.pax.adults }}</span>
                          <button @click="updatePax('adults', 1)" class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center font-bold hover:bg-gray-100 transition-colors">+</button>
                       </div>
                    </div>
                    <!-- Children -->
                    <div class="flex justify-between items-center">
                       <div>
                          <div class="font-bold text-gray-700">{{ $t('flight_form.children') }}</div>
                          <div class="text-xs text-gray-500">{{ $t('flight_form.children_desc') }}</div>
                       </div>
                       <div class="flex items-center space-x-3">
                          <button @click="updatePax('children', -1)" class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center font-bold hover:bg-gray-100 transition-colors" :disabled="form.pax.children <= 0">-</button>
                          <span class="w-4 text-center font-bold">{{ form.pax.children }}</span>
                          <button @click="updatePax('children', 1)" class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center font-bold hover:bg-gray-100 transition-colors">+</button>
                       </div>
                    </div>
                    <!-- Infants -->
                    <div class="flex justify-between items-center">
                       <div>
                          <div class="font-bold text-gray-700">{{ $t('flight_form.infants') }}</div>
                          <div class="text-xs text-gray-500">{{ $t('flight_form.infants_desc') }}</div>
                       </div>
                       <div class="flex items-center space-x-3">
                          <button @click="updatePax('infants', -1)" class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center font-bold hover:bg-gray-100 transition-colors" :disabled="form.pax.infants <= 0">-</button>
                          <span class="w-4 text-center font-bold">{{ form.pax.infants }}</span>
                          <button @click="updatePax('infants', 1)" class="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center font-bold hover:bg-gray-100 transition-colors">+</button>
                       </div>
                    </div>
                 </div>
                 <button @click="showPaxDropdown = false" class="w-full mt-4 bg-primary text-white font-bold py-2 rounded-lg hover:bg-opacity-90 transition-all">{{ $t('flight_form.confirm') }}</button>
              </div>
             <div v-if="showPaxDropdown" class="fixed inset-0 z-40 bg-transparent" @click="showPaxDropdown = false"></div>
          </div>
        </div>
    </div>
    
    <!-- Add Leg Button -->
    <div v-if="form.tripType === 'multicity'" class="mt-4">
        <button @click="addLeg" class="bg-red-600 text-white px-4 py-2 rounded-full font-bold text-sm hover:bg-red-700 transition-colors flex items-center">
            <span class="material-icons text-sm mr-1">add</span> {{ $t('flight_form.add_leg') }}
        </button>
    </div>

    <!-- Submit Action -->
    <div class="mt-6 flex justify-end">
         <button 
           @click="requestQuote"
           class="w-full md:w-auto px-8 h-[46px] bg-primary text-white font-bold rounded-lg shadow-lg hover:bg-opacity-90 transform hover:scale-[1.02] transition-all flex items-center justify-center uppercase tracking-wide text-sm"
        >
           {{ $t('flight_form.search') }}
         </button>
    </div>

    <!-- Contact Info Modal... (Same as before but with $t) -->
    <div v-if="showContactForm" class="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
        <div class="bg-white rounded-2xl shadow-2xl p-6 md:p-8 w-full max-w-lg relative animate-scale-in">
             <button @click="showContactForm = false" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><span class="material-icons">close</span></button>
             <h3 class="text-xl font-bold text-gray-800 mb-2">{{ $t('flight_form.contact_data') }}</h3>
              <div class="space-y-4">
                 <div>
                    <label class="block text-sm font-bold text-gray-700 mb-1">{{ $t('flight_form.email') }}</label>
                    <input type="email" v-model="form.contact.email" class="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all">
                 </div>
                 <div>
                    <label class="block text-sm font-bold text-gray-700 mb-1">{{ $t('flight_form.phone') }}</label>
                    <input type="tel" v-model="form.contact.phone" class="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-primary transition-all">
                 </div>
              </div>
              <button @click="submitQuote" class="w-full mt-8 py-4 bg-primary text-white font-bold rounded-xl shadow-lg" :disabled="submitting">
                 {{ submitting ? $t('flight_form.sending') : $t('flight_form.request_quote') }}
              </button>
              <p class="text-xs text-gray-400 mt-4 text-center">{{ $t('flight_form.terms_disclaimer') }}</p>
        </div>
    </div>
    
    <!-- Toast Error -->
    <div v-if="errorMsg" class="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-6 py-3 rounded-full shadow-lg z-[70] animate-bounce-in text-center font-medium text-sm">
        {{ errorMsg }}
    </div>

  </div>
</template>

<script>
import { canUpdatePax } from '@/utils/passengerValidation.mjs'
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';

export default {
  name: 'FlightQuoteForm',
  components: { DatePicker },
  data() {
    return {
      results: [], // Array of objects { origin: [], destination: [] } per leg
      loadingSearch: [], // Array of objects { origin: false, destination: false } to track loading state
      searchTimeout: null,
      submitting: false,
      showPaxDropdown: false,
      showContactForm: false,
      errorMsg: null,
      maxVisibleResults: 10, // Limit visible results for better performance
      form: {
        tripType: 'roundtrip',
        dateRange: [], // For roundtrip
        legs: [
            { origin: '', destination: '', date: '' }
        ],
        pax: {
          adults: 1,
          children: 0,
          infants: 0
        },
        contact: {
          email: '',
          phone: ''
        }
      }
    }
  },
  computed: {
    totalPax() {
      return this.form.pax.adults + this.form.pax.children + this.form.pax.infants
    },
    currentUser() {
      return this.$store.getters['auth/currentUser']
    },
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated']
    }
  },
  watch: {
    'form.tripType'(newVal) {
        if(newVal === 'roundtrip') {
           this.form.legs = [{ origin: '', destination: '', date: '' }] 
           this.form.dateRange = []
           this.loadingSearch = [{ origin: false, destination: false }]
        } else if (newVal === 'oneway') {
           this.form.legs = [{ origin: '', destination: '', date: '' }]
           this.loadingSearch = [{ origin: false, destination: false }]
        } else if (newVal === 'multicity') {
           if(this.form.legs.length === 1) {
               this.form.legs.push({ origin: '', destination: '', date: '' })
               this.loadingSearch.push({ origin: false, destination: false })
           }
        }
    }
  },
  created() {
    // Initialize results for first leg
    this.results[0] = { origin: [], destination: [] }
    this.loadingSearch[0] = { origin: false, destination: false }
  },
  methods: {
    addLeg() {
        this.form.legs.push({ origin: '', destination: '', date: '' })
        this.$set(this.results, this.form.legs.length - 1, { origin: [], destination: [] })
        this.$set(this.loadingSearch, this.form.legs.length - 1, { origin: false, destination: false })
    },
    removeLeg(index) {
        this.form.legs.splice(index, 1)
        this.results.splice(index, 1)
        this.loadingSearch.splice(index, 1)
    },
    togglePaxDropdown() {
      this.showPaxDropdown = !this.showPaxDropdown
    },
    updatePax(type, count) {
      const result = canUpdatePax(this.form.pax, type, count)
      
      if (!result.allowed) {
        if (result.message) {
            this.errorMsg = result.message
            setTimeout(() => { this.errorMsg = null }, 4000)
        }
        return
      }

      this.form.pax[type] += count
      this.errorMsg = null 
    },
    searchAirports(query, type, index) {
        // Clear previous timeout to prevent multiple requests
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout)
        }
        
        // Initialize results and loading arrays if needed
        if (!this.results[index]) this.$set(this.results, index, { origin: [], destination: [] })
        if (!this.loadingSearch[index]) this.$set(this.loadingSearch, index, { origin: false, destination: false })

        // Clear results and loading state immediately when user types (using $set for reactivity)
        this.$set(this.results[index], type, [])
        this.$set(this.loadingSearch[index], type, false)

        // If query is too short, don't search
        if (query.length < 3) {
            return
        }

        // Set loading state and debounce the API call
        this.searchTimeout = setTimeout(async () => {
            this.$set(this.loadingSearch[index], type, true)
            this.$forceUpdate() // Force re-render to show loading state
            try {
                const apiClient = require('@/services/api').default
                const response = await apiClient.get(`/airports/search?q=${query}`)
                // Use $set to ensure Vue detects the change
                this.$set(this.results[index], type, response.data)
                // Force Vue to re-render immediately to show dropdown
                this.$forceUpdate()
            } catch (error) {
                console.error('Error searching airports:', error)
                this.$set(this.results[index], type, [])
                this.$forceUpdate()
            } finally {
                this.$set(this.loadingSearch[index], type, false)
                this.$forceUpdate()
            }
        }, 400) // Increased to 400ms for better debouncing
    },
    getVisibleResults(results) {
        // Limit results to maxVisibleResults for better rendering performance
        return results.slice(0, this.maxVisibleResults)
    },
    selectAirport(airport, type, index) {
        this.form.legs[index][type] = `${airport.city} (${airport.code})`
        this.results[index][type] = []
    },
    async requestQuote() {
        this.errorMsg = null
        
        // Validate all legs
        for(let i=0; i < this.form.legs.length; i++) {
             const leg = this.form.legs[i]
             if(!leg.origin || !leg.destination) {
                 this.errorMsg = this.$t('flight_form.complete_leg', { msg1: i + 1 })
                 setTimeout(() => this.errorMsg = null, 4000)
                 return
             }
             
             if (this.form.tripType === 'roundtrip' && i===0) {
                 if(!this.form.dateRange || this.form.dateRange.length < 2 || !this.form.dateRange[0]) {
                     this.errorMsg = this.$t('flight_form.select_dates')
                     setTimeout(() => this.errorMsg = null, 4000)
                     return
                 }
             } else {
                 if(!leg.date) {
                     this.errorMsg = this.$t('flight_form.select_date_leg', { msg1: i + 1 })
                     setTimeout(() => this.errorMsg = null, 4000)
                     return
                 }
             }
        }
        
        // Check if user is truly authenticated by verifying session
        try {
          await this.$store.dispatch('auth/checkSession')
          
          // If we reach here, user is authenticated
          if (this.currentUser && this.currentUser.email && this.currentUser.phone) {
            // User is authenticated and has complete contact info
            this.form.contact.email = this.currentUser.email
            this.form.contact.phone = this.currentUser.phone
            
            // Submit directly without showing modal
            this.submitQuote()
            return
          } else if (this.currentUser) {
            // User is authenticated but missing some contact info
            this.form.contact.email = this.currentUser.email || ''
            this.form.contact.phone = this.currentUser.phone || ''
          }
        } catch (error) {
          // Session expired or user not authenticated
          console.log('User not authenticated or session expired')
          
          // If there was a stored token but it's invalid, notify user
          if (localStorage.getItem('token')) {
            this.$swal({
              icon: 'warning',
              title: this.$t('flight_form.session_expired_title'),
              text: this.$t('flight_form.session_expired_text'),
              confirmButtonText: this.$t('common.ok'),
              confirmButtonColor: '#e63946'
            })
          }
        }
        
        // Show contact form (either user not authenticated or missing contact info)
        this.showContactForm = true
    },
    async submitQuote() {
      this.submitting = true
      this.errorMsg = null
      
      // Validate contact information
      if (!this.form.contact.email || !this.form.contact.phone) {
        this.errorMsg = this.$t('flight_form.complete_contact')
        this.submitting = false
        setTimeout(() => this.errorMsg = null, 4000)
        return
      }
      
      try {
        const payload = {
            trip_type: this.form.tripType,
            legs: this.form.legs.map((leg, index) => {
                if (this.form.tripType === 'roundtrip' && index === 0) {
                    return {
                        ...leg,
                        departure_date: this.form.dateRange[0],
                        return_date: this.form.dateRange[1]
                    }
                }
                return {
                    ...leg,
                    departure_date: leg.date
                }
            }),
            passengers: this.form.pax,
            contact: this.form.contact
        }

        // Debug: Log payload to verify structure
        console.log('Flight Quote Payload:', JSON.stringify(payload, null, 2))

        const apiClient = require('@/services/api').default
        const response = await apiClient.post('/quotes/flight', payload)
        
        console.log('📊 Response received:', {
          status: response.status,
          statusText: response.statusText,
          data: response.data
        })
        
        // Check if response is successful (status 200 or 201)
        if (response && (response.status === 200 || response.status === 201)) {
          console.log('✅ Quote submitted successfully:', response.data)
          
          // Success message with Toast (less invasive)
          this.$swal({
            toast: true,
            position: 'top-end',
            icon: 'success',
            title: this.$t('flight_form.quote_success_title'),
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            background: '#e63946',
            color: '#ffffff',
            iconColor: '#ffffff'
          })
          
          // Reset entire form
          this.form = {
            tripType: 'roundtrip',
            dateRange: [],
            legs: [
              { origin: '', destination: '', date: '' }
            ],
            pax: {
              adults: 1,
              children: 0,
              infants: 0
            },
            contact: {
              email: '',
              phone: ''
            }
          }
          
          // Reset UI state
          this.showContactForm = false
          this.showPaxDropdown = false
          this.results = []
          this.loadingSearch = []
        } else {
          console.warn('⚠️ Unexpected response status:', response.status)
        }

      } catch (error) {
        console.error('❌ Error submitting quote:', error)
        
        // Check if it's actually a success response with 201 status
        if (error.response && error.response.status === 201) {
          console.log('✅ Quote submitted successfully (201):', error.response.data)
          
          // Show success message
          this.$swal({
            icon: 'success',
            title: this.$t('flight_form.quote_success_title'),
            text: this.$t('flight_form.quote_success_message'),
            confirmButtonText: this.$t('common.ok'),
            confirmButtonColor: '#e63946'
          })
          
          // Reset entire form
          this.form = {
            tripType: 'roundtrip',
            dateRange: [],
            legs: [
              { origin: '', destination: '', date: '' }
            ],
            pax: {
              adults: 1,
              children: 0,
              infants: 0
            },
            contact: {
              email: '',
              phone: ''
            }
          }
          
          // Reset UI state
          this.showContactForm = false
          this.showPaxDropdown = false
          this.results = []
          this.loadingSearch = []
        } else if (error.response && error.response.status === 401) {
          // Session expired - don't show error, user will be redirected by interceptor
          console.log('⚠️ Session expired during quote submission')
          // Don't set errorMsg, the interceptor will handle logout and redirect
        } else {
          // Real error
          this.errorMsg = error.response?.data?.message || this.$t('flight_form.error_sending')
        }
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
/* Make calendar icons white */
:deep(.mx-icon-calendar),
:deep(.mx-icon-clear) {
  color: white !important;
  fill: white !important;
}

:deep(.mx-input-wrapper .mx-input) {
  color: white !important;
}

:deep(.mx-icon-calendar svg),
:deep(.mx-icon-clear svg) {
  fill: white !important;
}
</style>
